import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { FileText, Filter, Download } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { formatApiError } from '../lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Safely render any value (string, number, bool, object, array, null) as text
 * for a table cell. Objects/arrays are compacted to JSON so the Audit Log page
 * never crashes with "Objects are not valid as a React child" — a single row
 * carrying a dict in `new_value` used to blank the whole page.
 */
const renderCell = (val) => {
  if (val === null || val === undefined || val === '') return 'N/A';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
  try {
    const s = JSON.stringify(val);
    return s.length > 80 ? s.slice(0, 77) + '...' : s;
  } catch {
    return String(val);
  }
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resourceFilter, setResourceFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = resourceFilter !== 'all' ? { resource: resourceFilter } : {};
      const { data } = await axios.get(`${API_URL}/audit-logs`, {
        params,
        withCredentials: true,
      });
      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to fetch audit logs'));
    } finally {
      setLoading(false);
    }
  }, [resourceFilter]);

  useEffect(() => {
    fetchLogs();
  }, [resourceFilter, fetchLogs]);


  const getActionBadgeColor = (action) => {
    const colors = {
      create: 'bg-green-50 text-green-700 border-green-200',
      update: 'bg-blue-50 text-blue-700 border-blue-200',
      delete: 'bg-red-50 text-red-700 border-red-200',
      deactivate: 'bg-amber-50 text-amber-700 border-amber-200',
      login: 'bg-purple-50 text-purple-700 border-purple-200',
      logout: 'bg-slate-50 text-slate-700 border-slate-200',
    };
    return colors[action] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const resourceTypes = [
    { value: 'all', label: 'All Resources' },
    { value: 'user', label: 'Users' },
    { value: 'company', label: 'Companies' },
    { value: 'organizational_unit', label: 'Org Units' },
    { value: 'tenant', label: 'Tenants' },
    { value: 'auth', label: 'Authentication' },
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <FileText className="inline-block w-8 h-8 mr-2 mb-1" />
              Audit Logs
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Complete audit trail of all system activities
            </p>
          </div>
          <button data-testid="audit-export-btn" onClick={() => {
            if (logs.length === 0) { toast.error('No logs to export'); return; }
            downloadCSV(logs.map(l => ({
              Timestamp: l.timestamp, User_ID: l.user_id, Action: l.action, Resource: l.resource, Resource_ID: l.resource_id || '', IP: l.ip_address || ''
            })), 'audit_logs.csv');
            toast.success('Audit logs exported');
          }} className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-md text-sm font-medium">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filter by:</span>
          </div>
          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger className="w-48" data-testid="audit-log-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {resourceTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Resource
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  User ID
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-slate-500">
                    Loading...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-slate-500">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  // Prefer new_value (what changed); fall back to resource_id.
                  // Both may be objects / arrays — renderCell handles them safely.
                  const detail = log.new_value ?? log.resource_id;
                  let ts = log.timestamp;
                  try { ts = log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'; } catch { ts = String(log.timestamp); }
                  return (
                    <tr
                      key={log.id}
                      data-testid={`audit-log-row-${log.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-3 px-4 text-sm text-slate-700">{ts}</td>
                      <td className="py-3 px-4 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getActionBadgeColor(
                            log.action
                          )}`}
                        >
                          {String(log.action || 'unknown')}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700 font-medium">
                        {String(log.resource || '-')}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700 font-mono text-xs">
                        {log.user_id ? `${String(log.user_id).substring(0, 8)}...` : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700" title={typeof detail === 'object' && detail ? JSON.stringify(detail) : ''}>
                        {renderCell(detail)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <p className="text-sm text-slate-600">
            Showing <span className="font-medium text-slate-900">{logs.length}</span> audit log
            entries. All actions are permanently logged for compliance and security.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default AuditLogs;
